import { ASSISTANTS_SEARCH_MAX_LENGTH } from "../constants/assistants";
import type {
  EAssistantType,
  IAssistant,
  IAssistantsPage,
  ICreateOrUpdateAssistantPayload,
  IGetAssistantsPageParams,
  IPresignFileUploadParams,
  IPresignFileUploadResponse,
  IReleaseFileUploadsParams,
  IReleaseFileUploadsResult,
  IUpdateAssistantParams,
} from "../types";
import { encodePathSegment } from "../utils/encode-path-segment";
import type { ApiClient } from "./client";

export class AssistantsApi {
  constructor(private readonly apiClient: ApiClient) {}

  async presignFileUpload(
    params: IPresignFileUploadParams,
  ): Promise<IPresignFileUploadResponse> {
    const { signal, ...body } = params;
    const response = await this.apiClient.post<IPresignFileUploadResponse>(
      "/assistants/files/presign",
      body,
      { ...(signal !== undefined && { signal }) },
    );

    return response.data;
  }

  // NOTE: Delete uncommitted presigned uploads. Used on submit success (to drop
  // X-removed keys), Cancel button, in-app navigation, and hardware Back.
  async releaseFileUploads(
    params: IReleaseFileUploadsParams,
  ): Promise<IReleaseFileUploadsResult> {
    const response = await this.apiClient.post<IReleaseFileUploadsResult>(
      "/assistants/files/release",
      params,
    );
    return response.data;
  }

  // NOTE: Fire-and-forget variant for `pagehide`/`beforeunload`. Cannot await
  // during page unload — the browser completes the request via `fetch keepalive`.
  releaseFileUploadsBeacon(params: IReleaseFileUploadsParams): void {
    this.apiClient.postKeepalive("/assistants/files/release", params);
  }

  async getAssistants(type: EAssistantType): Promise<IAssistant[]> {
    const response = await this.apiClient.get<IAssistant[]>(
      `/assistants?type=${type}`,
    );
    return response.data;
  }

  // NOTE: NSAI-6814 — the Assistants page's list, one page at a time. Callers
  // that need every assistant at once (chat picker, sidebar) keep using
  // getAssistants above.
  async getAssistantsPage({
    type,
    page,
    limit,
    search,
  }: IGetAssistantsPageParams = {}): Promise<IAssistantsPage> {
    const params = new URLSearchParams();
    if (type !== undefined) {
      params.set("type", type);
    }
    if (page !== undefined) {
      params.set("page", String(page));
    }
    if (limit !== undefined) {
      params.set("limit", String(limit));
    }
    const trimmedSearch = search?.trim().slice(0, ASSISTANTS_SEARCH_MAX_LENGTH);
    if (trimmedSearch) {
      params.set("search", trimmedSearch);
    }
    const query = params.toString();
    const response = await this.apiClient.get<IAssistantsPage>(
      query ? `/assistants/paginated?${query}` : "/assistants/paginated",
    );
    return response.data;
  }

  async getByUuid(uuid: string): Promise<IAssistant> {
    const response = await this.apiClient.get<IAssistant>(
      `/assistants/${encodePathSegment(uuid)}`,
    );
    return response.data;
  }

  async create(data: ICreateOrUpdateAssistantPayload): Promise<IAssistant> {
    const response = await this.apiClient.post<IAssistant>("/assistants", data);
    return response.data;
  }

  async createTemporary(
    data: ICreateOrUpdateAssistantPayload,
  ): Promise<IAssistant> {
    const response = await this.apiClient.post<IAssistant>(
      "/assistants/temporary",
      data,
    );
    return response.data;
  }

  async activate(uuid: string): Promise<IAssistant> {
    const response = await this.apiClient.put<IAssistant>(
      `/assistants/${encodePathSegment(uuid)}/activate`,
    );
    return response.data;
  }

  async update({ uuid, data }: IUpdateAssistantParams): Promise<IAssistant> {
    const response = await this.apiClient.put<IAssistant>(
      `/assistants/${encodePathSegment(uuid)}`,
      data,
    );
    return response.data;
  }

  async delete(uuid: string): Promise<void> {
    await this.apiClient.delete(`/assistants/${encodePathSegment(uuid)}`);
  }
}
